import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { Announcement } from "../../../../models/announcement/announcement";
import { AnnouncementCategoryText } from "../../../../models/announcement/announcement-category-text";
import { DropdownValue } from "../../../../models/dropdown-value";
import { AnnouncementService } from "../../../../services/announcement.service";
import { EventContainerService } from "../../../../services/event-container.service";
import { PopupService } from "../../../../services/popup.service";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
  standalone: false,
  selector: "dua-announcement-text",
  templateUrl: "./announcement-text.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./announcement-text.component.scss"],
})
export class AnnouncementTextComponent implements OnInit, OnDestroy {
  @Input() announcement!: Announcement;
  @Input() createMode = false;
  @Input() disabledForEdit = false;

  announcementCategories: DropdownValue[] = [];
  announcementTexts: AnnouncementCategoryText[] = [];
  filteredAnnouncementTexts: AnnouncementCategoryText[] = [];
  open = true;
  filterText = "";
  announcementReplacementText = "";
  showAll = false;
  readonly defaultDisplayedNumberOfTexts = 6;
  selectedTextCode = "";

  private selectAnnouncementSubscription!: Subscription;
  private categoriesSubscription!: Subscription;

  constructor(
    private announcementService: AnnouncementService,
    private eventContainerService: EventContainerService,
    private popupService: PopupService
  ) {}

  ngOnInit(): void {
    this.populateCategories();
    this.categoriesSubscription = this.announcementService
      .getAnnouncementCategoriesObservable()
      .subscribe(() => this.populateCategories());
    this.populateAnnouncementTexts();
    this.setAnnouncementReplacementText();
    this.selectedTextCode =
      this.announcementService.selectedAnnouncementCategoryText?.code ?? "";
    this.selectAnnouncementSubscription = this.announcementService
      .getSelectAnnouncementText()
      .subscribe((selected) => {
        this.selectedTextCode = selected?.code ?? "";
      });
  }

  ngOnDestroy(): void {
    this.selectAnnouncementSubscription?.unsubscribe();
    this.categoriesSubscription?.unsubscribe();
  }

  populateCategories() {
    this.announcementCategories = this.announcementService
      .getAnnouncementCategories()
      .map((x) => new DropdownValue(x.displayName, x.categoryType));
  }

  announcementCategoryChange(event: { value: string }) {
    const selectedValue = event?.value;
    const category = this.announcementCategories.find(
      (x) => x.value === selectedValue
    );
    if (!category) {
      return;
    }

    this.announcement.categoryType = category.value;
    const categoryObject = new AnnouncementCategoryText({
      id: 0,
      code: category.value,
      text: category.label,
      externalOperatorCanUse: false,
      properties: [],
    });
    this.announcementService.emitSelectAnnouncementText(categoryObject);
    this.populateAnnouncementTexts();

    if (this.announcement?.categoryText?.properties) {
      this.announcement.categoryText.properties = [];
    }
  }

  inputValueChange(event: string) {
    this.filterText = event;
    this.filterAnnouncementTexts();
  }

  populateAnnouncementTexts() {
    const prefix = this.announcementService.getAnnouncementCategoryByType(
      this.announcement?.categoryType
    )?.prefix;

    if (prefix) {
      this.announcementService.getAnnouncementTextList(prefix).subscribe(
        (textList) => {
          this.eventContainerService.loading = false;
          this.announcementTexts = textList;
          this.showAll = false;
          this.filterAnnouncementTexts();
        },
        (error: HttpErrorResponse) => {
          console.error(error);
          this.popupService.setErrorPopup(error);
          this.eventContainerService.loading = false;
        }
      );
    } else {
      this.announcementTexts = [];
      this.filteredAnnouncementTexts = [];
    }
  }

  setAnnouncementReplacementText() {
    this.announcementReplacementText = this.announcementService.getAnnouncementReplacementText(
      this.announcement
    );
  }

  selectAnnouncementText(text: AnnouncementCategoryText) {
    if (this.disabledForEdit) {
      return;
    }

    if (
      this.announcementService.selectedAnnouncementCategoryText?.code !==
      text?.code
    ) {
      this.announcementService.emitSelectAnnouncementText(text);
    }

    this.announcement.categoryText = text;
  }

  showAllTexts() {
    this.showAll = true;
  }

  showLess() {
    this.showAll = false;
  }

  get displayedAnnouncementTexts(): AnnouncementCategoryText[] {
    if (this.showAll) {
      return this.filteredAnnouncementTexts;
    }
    return this.filteredAnnouncementTexts.slice(
      0,
      this.defaultDisplayedNumberOfTexts
    );
  }

  private filterAnnouncementTexts() {
    this.filteredAnnouncementTexts = this.announcementTexts.filter(
      (x) =>
        !this.filterText ||
        x.text.toLowerCase().includes(this.filterText.toLowerCase())
    );
  }
}
